import { useEffect } from 'react'
import { useStore } from '@nanostores/react'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'

import type { BlockedReason, CatalogPlan, WorkspaceBillingState } from '~/features/billing/types'

import { Button } from '~/elements/Button'
import { Dialog, DialogFooter, DialogTitle } from '~/elements/Dialog'
import { $user } from '~/features/auth/stores/user'
import { usePlatformSettings } from '~/features/auth/hooks/useAuthQueries'
import {
  useBillingPortal,
  useCurrentWorkspacePlan,
  useSubscriptionMutation
} from '~/features/billing/hooks/useBillingHooks'
import { $billingBlockedDialog, closeBillingBlockedDialog } from '~/features/billing/stores/blocked'
import { formatDate, formatGb, formatUsd, isPaidPlan } from '~/features/billing/utils'
import { getRoutePath } from '~/lib/router'
import { cn } from '~/lib/utils'

type BlockedAction =
  | { kind: 'link'; label: string; href: string }
  | { kind: 'portal'; label: string }
  | { kind: 'addon'; label: string; addon: 'extraSeats' | 'extraProjects'; price?: string }

type BlockedCopy = {
  title: string
  description: string
  action: BlockedAction
  ownerHint: string
}

const billingHref = (section?: string) =>
  `${getRoutePath('workspaceBilling')}${section ? `?section=${section}` : ''}`

const upgrade: BlockedAction = { kind: 'link', label: 'Upgrade plan', href: billingHref() }

export function getBlockedCopy(
  reason: BlockedReason | null,
  state?: WorkspaceBillingState,
  plan?: CatalogPlan
): BlockedCopy {
  const resetsOn = state?.period ? ` until ${formatDate(state.period.end)}` : ''
  const paid = isPaidPlan(state?.plan ?? plan?.id)
  const interval = state?.interval ?? 'month'
  const addonPrice = (key: 'extraSeat' | 'extraProject') => {
    const addon = plan?.addons?.[key]
    return addon ? `${formatUsd(addon[interval])}/${interval}` : undefined
  }

  switch (reason) {
    case 'free_storage_full':
      return {
        title: 'Free storage is full',
        description: `This workspace uses all ${formatGb(state?.usage.storageGb.included ?? null)} of Free storage. New writes, imports and relationships are paused; reads keep working. Upgrade to keep writing.`,
        action: upgrade,
        ownerHint: 'Ask a workspace owner to upgrade the plan.'
      }
    case 'free_ai_exhausted':
      return {
        title: 'Free AI allowance used',
        description: `Smart Search and managed embeddings are paused${resetsOn}. Upgrade for a larger allowance.`,
        action: upgrade,
        ownerHint: 'Ask a workspace owner to upgrade the plan.'
      }
    case 'spend_cap_reached':
      return {
        title: 'Spend cap reached',
        description: `This usage window reached its spend cap${state?.spendCap.amount != null ? ` of ${formatUsd(state.spendCap.amount)}` : ''}. Writes and AI actions are paused${resetsOn}. Raise the cap to resume now.`,
        action: { kind: 'link', label: 'Raise spend cap', href: billingHref('spend-cap') },
        ownerHint: 'Ask a workspace owner to raise the spend cap.'
      }
    case 'past_due':
      return {
        title: 'Payment failed',
        description:
          'We could not charge your payment method. Update it to keep your subscription active and lift the pause.',
        action: { kind: 'portal', label: 'Update payment method' },
        ownerHint: 'Ask a workspace owner to update the payment method.'
      }
    case 'canceled':
      return {
        title: 'Subscription canceled',
        description: 'This workspace no longer has an active subscription. Resubscribe to restore your plan.',
        action: { kind: 'link', label: 'Resubscribe', href: billingHref() },
        ownerHint: 'Ask a workspace owner to resubscribe.'
      }
    case 'project_limit':
      return paid ?
          {
            title: 'Project limit reached',
            description: `This workspace uses all ${state?.limits.projects ?? ''} projects on its plan. Add an extra project${addonPrice('extraProject') ? ` for ${addonPrice('extraProject')}, prorated from today,` : ''} to create another one.`,
            action: {
              kind: 'addon',
              label: 'Buy an extra project',
              addon: 'extraProjects',
              price: addonPrice('extraProject')
            },
            ownerHint: 'Ask a workspace owner to add a project.'
          }
        : {
            title: 'Project limit reached',
            description: 'The Free plan includes a limited number of projects. Upgrade to create more.',
            action: upgrade,
            ownerHint: 'Ask a workspace owner to upgrade the plan.'
          }
    case 'seat_limit':
      return paid ?
          {
            title: 'Seat limit reached',
            description: `This workspace uses all ${state?.limits.seats ?? ''} seats on its plan. Add an extra seat${addonPrice('extraSeat') ? ` for ${addonPrice('extraSeat')}, prorated from today,` : ''} to invite another member.`,
            action: {
              kind: 'addon',
              label: 'Buy an extra seat',
              addon: 'extraSeats',
              price: addonPrice('extraSeat')
            },
            ownerHint: 'Ask a workspace owner to add a seat.'
          }
        : {
            title: 'Seat limit reached',
            description: 'The Free plan includes a single seat. Upgrade to invite your team.',
            action: upgrade,
            ownerHint: 'Ask a workspace owner to upgrade the plan.'
          }
    default:
      return {
        title: 'Plan limit reached',
        description:
          'This action is not available on your current plan. Review your plan and usage to continue.',
        action: { kind: 'link', label: 'View billing', href: billingHref() },
        ownerHint: 'Ask a workspace owner to review the plan.'
      }
  }
}

function useBlockedActionHandler() {
  const { state } = useCurrentWorkspacePlan()
  const portal = useBillingPortal()
  const { mutateAsync: updateSubscription, isPending } = useSubscriptionMutation()

  const run = async (action: BlockedAction) => {
    if (action.kind === 'portal') {
      await portal.open('updatePaymentMethod')
      return true
    }
    if (action.kind === 'addon' && state) {
      const quantities = { ...state.quantities }
      quantities[action.addon] += 1
      await updateSubscription({ action: 'set_addons', ...quantities })
      return true
    }
    return false
  }

  return { run, isPending: isPending || portal.isPending }
}

function ActionButton({
  action,
  onDone,
  size,
  className
}: {
  action: BlockedAction
  onDone?: () => void
  size?: 'small' | 'medium'
  className?: string
}) {
  const { run, isPending } = useBlockedActionHandler()

  if (action.kind === 'link') {
    return (
      <Button as="a" className={className} href={action.href} onClick={onDone} size={size} variant="primary">
        {action.label}
      </Button>
    )
  }

  return (
    <Button
      className={className}
      loading={isPending}
      onClick={() =>
        run(action)
          .then(() => onDone?.())
          .catch(() => undefined)
      }
      size={size}
      variant="primary"
    >
      {action.label}
      {action.kind === 'addon' && action.price ? ` · ${action.price}` : null}
    </Button>
  )
}

const useIsOwner = () => useStore($user).currentScope?.role === 'owner'

export function BillingBlockedBanner() {
  const { data: platformSettings } = usePlatformSettings()
  const { state, plan } = useCurrentWorkspacePlan()
  const isOwner = useIsOwner()

  const reason = state?.blocked.reason ?? (state?.status === 'past_due' ? 'past_due' : null)

  if (platformSettings?.selfHosted || !state || !reason) return null

  const copy = getBlockedCopy(reason, state, plan)
  const severe = state.blocked.writes || state.blocked.ai || reason === 'past_due'

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm font-medium',
        severe ?
          'border-b border-danger/20 bg-danger/15 text-danger'
        : 'border-b border-warning/20 bg-warning/10 text-warning'
      )}
      role="alert"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1">
        <strong>{copy.title}.</strong> {copy.description}
        {!isOwner && ` ${copy.ownerHint}`}
      </span>
      {isOwner && <ActionButton action={copy.action} className="shrink-0" size="small" />}
    </div>
  )
}

export function BillingBlockedDialog() {
  const { open, reason: errorReason } = useStore($billingBlockedDialog)
  const { state, plan } = useCurrentWorkspacePlan()
  const queryClient = useQueryClient()
  const isOwner = useIsOwner()

  useEffect(() => {
    // The 402 may not carry a reason (SDK errors); refresh the state to learn it.
    if (open) {
      void queryClient.invalidateQueries({ queryKey: ['billing', 'state'] })
    }
  }, [open, queryClient])

  const copy = getBlockedCopy(errorReason ?? state?.blocked.reason ?? null, state, plan)

  return (
    <Dialog
      className="gap-5"
      onOpenChange={(next) => (next ? undefined : closeBillingBlockedDialog())}
      open={open}
    >
      <DialogTitle className="items-center">
        <AlertTriangle className="h-5 w-5 text-danger" /> {copy.title}
      </DialogTitle>

      <p className="text-content2">
        {copy.description}
        {!isOwner && ` ${copy.ownerHint}`}
      </p>

      <DialogFooter>
        <Button onClick={closeBillingBlockedDialog} variant="secondary">
          Close
        </Button>
        {isOwner && <ActionButton action={copy.action} onDone={closeBillingBlockedDialog} />}
      </DialogFooter>
    </Dialog>
  )
}
